import { jsx, jsxs } from "react/jsx-runtime";
import { useThemeProps } from "@mui/material";
import { forwardRef, useImperativeHandle } from "react";
import { useSlotProps } from "../../base/index.js";
import { ChevronRight } from "../../icons/index.js";
import { actionHeaderName } from "./classes.js";
import { useHeaderState } from "./hooks/useHeaderState.js";
import { useUtilityClasses } from "./hooks/useUtilityClasses.js";
import { Chevron } from "./components/Chevron.js";
import { PrimaryAction } from "./components/PrimaryAction.js";
import { Root } from "./components/Root.js";
import { SecondaryAction } from "./components/SecondaryAction.js";
import { Subtitle } from "./components/Subtitle.js";
import { Title } from "./components/Title.js";
const defaultOpenIcon = /* @__PURE__ */ jsx(ChevronRight, { fontSize: "inherit", sx: { rotate: "90deg" } });
const defaultClosedIcon = /* @__PURE__ */ jsx(ChevronRight, { fontSize: "inherit" });
const ActionHeader = forwardRef(function ActionHeader2(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: actionHeaderName
  });
  const {
    title,
    actions,
    subtitle,
    onChange,
    disabled,
    hideIcon,
    components,
    disableButton,
    primaryAction,
    titleClickable,
    componentsProps,
    size = "medium",
    secondaryAction,
    defaultExpanded,
    openIcon = defaultOpenIcon,
    expanded: controlledExpanded,
    testId = "tidal-actionheader",
    closedIcon = defaultClosedIcon,
    primaryActionVisibility = "always",
    secondaryActionVisibility = "always",
    ...rest
  } = props;
  const { expanded, onOpen, onClose, onToggle } = useHeaderState({
    onChange,
    defaultExpanded,
    expanded: controlledExpanded
  });
  useImperativeHandle(
    actions,
    () => ({
      open: onOpen,
      close: onClose,
      toggle: onToggle
    }),
    [onClose, onOpen, onToggle]
  );
  const icon = expanded ? openIcon : closedIcon;
  const ownerState = {
    ...props,
    size,
    expanded,
    primaryActionVisibility,
    secondaryActionVisibility
  };
  const utilityClasses = useUtilityClasses(ownerState);
  const rootSlotProps = useSlotProps({
    ownerState,
    elementType: Root,
    externalForwardedProps: rest,
    className: utilityClasses.root,
    externalSlotProps: componentsProps?.root,
    additionalProps: { ref, as: components?.Root, "data-testid": testId }
  });
  const chevronSlotProps = useSlotProps({
    ownerState,
    elementType: Chevron,
    className: utilityClasses.chevron,
    externalSlotProps: componentsProps?.chevron,
    additionalProps: {
      size,
      onClick: onToggle,
      as: components?.Chevron,
      disabled: disableButton || disabled,
      "data-testid": "tidal-actionheader-chevron"
    }
  });
  const titleSlotProps = useSlotProps({
    ownerState,
    elementType: Title,
    className: utilityClasses.title,
    externalSlotProps: componentsProps?.title,
    additionalProps: {
      as: components?.Title,
      "data-testid": "tidal-actionheader-title",
      onClick: titleClickable ? onToggle : void 0,
      noWrap: true
    }
  });
  const subtitleSlotProps = useSlotProps({
    ownerState,
    elementType: Subtitle,
    className: utilityClasses.subtitle,
    externalSlotProps: componentsProps?.subtitle,
    additionalProps: {
      as: components?.Subtitle,
      "data-testid": "tidal-actionheader-subtitle",
      noWrap: true
    }
  });
  const primaryActionSlotProps = useSlotProps({
    ownerState,
    externalSlotProps: {},
    elementType: PrimaryAction,
    className: utilityClasses.primaryAction,
    additionalProps: { "data-testid": "tidal-actionheader-primaryAction" }
  });
  const secondaryActionSlotProps = useSlotProps({
    ownerState,
    externalSlotProps: {},
    elementType: SecondaryAction,
    className: utilityClasses.secondaryAction,
    additionalProps: { "data-testid": "tidal-actionheader-secondaryAction" }
  });
  return /* @__PURE__ */ jsxs(Root, { ...rootSlotProps, "data-tidal": "ActionHeader", children: [
    !hideIcon && /* @__PURE__ */ jsx(Chevron, { ...chevronSlotProps, children: icon }),
    /* @__PURE__ */ jsx(Title, { ...titleSlotProps, children: title }),
    !!subtitle && /* @__PURE__ */ jsx(Subtitle, { ...subtitleSlotProps, children: subtitle }),
    !!primaryAction && /* @__PURE__ */ jsx(PrimaryAction, { ...primaryActionSlotProps, children: primaryAction }),
    !!secondaryAction && /* @__PURE__ */ jsx(SecondaryAction, { ...secondaryActionSlotProps, children: secondaryAction })
  ] });
});
export {
  ActionHeader
};
//# sourceMappingURL=ActionHeader.js.map
